// One agent rendered as a draggable node card. Layout matches the bento
// mockup; drag is delegated via the `onPointerDown` handler on Canvas
// so we can update the world transform + persist on drop.

import { Heartbeat } from "./Heartbeat"
import { useTranslations } from "../../lib/translations"

export function Node({ agent, dimmed, focused, onPointerDown, onClick, onMouseEnter, onMouseLeave }) {
  const t = useTranslations()
  const classes = ["cm-node"]
  if (agent.master) classes.push("master")
  if (dimmed) classes.push("dim")
  if (focused) classes.push("focus")
  if (agent._dragging) classes.push("dragging")

  const relText = agent.master
    ? `${anthropicShort(agent.anthropic_agent_id)} · ${t("canvas.tip.master")}`
    : agent.manager_id
      ? `${anthropicShort(agent.anthropic_agent_id)} · ${t("canvas.tip.reports_to", { name: "—" })}`
      : `${anthropicShort(agent.anthropic_agent_id)} · ${t("canvas.tip.no_manager")}`

  const activity = activityText(agent, t)
  const pending = agent.pending_approvals_count || 0

  return (
    <div className={classes.join(" ")}
         style={{ left: agent.x, top: agent.y }}
         onPointerDown={onPointerDown}
         onClick={onClick}
         onMouseEnter={onMouseEnter}
         onMouseLeave={onMouseLeave}>
      {pending > 0 && <div className="cm-badge">{pending}</div>}
      <div className="cm-node-top">
        <div className="cm-node-role">
          <b>{agent.name}</b>
          <small>{relText}</small>
        </div>
        <Heartbeat status={agent.status} pendingApprovals={pending} />
      </div>
      <div className="cm-node-activity">{activity}</div>
      <div className="cm-node-foot">
        <CanaryBadge summary={agent.canary_summary} t={t} />
        {pending > 0
          ? <span className="cm-approve">✋ sign off <span className="cnt">{pending}</span></span>
          : <span className="cm-spacer" />}
      </div>
    </div>
  )
}

function CanaryBadge({ summary, t }) {
  // M7: color-coded by the worst-of overall canary outcome.
  // `summary` is null when no canaries are declared for this agent.
  const overall = summary?.overall || "none"
  const color = {
    passed:  "var(--ok)",
    failed:  "var(--bad)",
    errored: "var(--bad)",
    unknown: "var(--warn)",
    none:    "var(--ink-3)",
  }[overall]

  const title = overall === "none"
    ? t("canvas.tip.canary_label")
    : `canary · ${overall}${summary?.kinds?.length ? ` (${summary.kinds.length})` : ""}`

  return (
    <span className="cm-canary" title={title}>
      <i style={{ background: color }} />canary
    </span>
  )
}

function anthropicShort(id) {
  if (!id) return "—"
  return id.length > 14 ? id.slice(0, 6) + "…" + id.slice(-4) : id
}

function activityText(agent, t) {
  if (agent.status === "paused")   return t("modal.status.paused")
  if (agent.status === "disabled") return t("modal.status.disabled")
  if ((agent.pending_approvals_count || 0) > 0) return "awaiting your sign-off"
  return "Idle — ready for work"
}
